import { motion } from 'framer-motion';
import { 
  ArrowLeft, CreditCard, Smartphone, QrCode, Shield, 
  Clock, CheckCircle, AlertCircle, Globe, Zap,
  ArrowRight, Info
} from 'lucide-react';
import { useLanguage } from './i18n';

const COLORS = {
  primary: '#22c55e',
  primaryDark: '#16a34a',
  dark: '#0f172a',
  darkLight: '#1e293b',
  gray: '#64748b',
  grayLight: '#94a3b8',
  white: '#ffffff',
  bg: '#f8fafc'
};

// Payment system logos as SVG components
const MirLogo = () => (
  <svg viewBox="0 0 100 40" className="h-8">
    <rect fill="#4DB45E" width="100" height="40" rx="4"/>
    <text x="50" y="26" textAnchor="middle" fill="white" fontSize="16" fontWeight="bold">МИР</text>
  </svg>
);

const SbpLogo = () => (
  <svg viewBox="0 0 100 40" className="h-8">
    <rect fill="#5C2D91" width="100" height="40" rx="4"/>
    <text x="50" y="26" textAnchor="middle" fill="white" fontSize="14" fontWeight="bold">СБП</text>
  </svg>
);

const VisaLogo = () => (
  <svg viewBox="0 0 100 40" className="h-8">
    <rect fill="#1A1F71" width="100" height="40" rx="4"/>
    <text x="50" y="28" textAnchor="middle" fill="white" fontSize="18" fontWeight="bold" fontStyle="italic">VISA</text>
  </svg>
);

const MastercardLogo = () => (
  <svg viewBox="0 0 100 40" className="h-8">
    <rect fill="#000" width="100" height="40" rx="4"/>
    <circle cx="38" cy="20" r="14" fill="#EB001B"/>
    <circle cx="62" cy="20" r="14" fill="#F79E1B"/>
    <circle cx="50" cy="20" r="8" fill="#FF5F00"/>
  </svg>
);

const MiaLogo = () => (
  <svg viewBox="0 0 100 40" className="h-8">
    <rect fill="#00A651" width="100" height="40" rx="4"/>
    <text x="50" y="26" textAnchor="middle" fill="white" fontSize="14" fontWeight="bold">MIA</text>
  </svg>
);

const PaymentMethodsPage = () => {
  const { t } = useLanguage();

  const paymentMethods = [
    {
      id: 'sbp',
      name: 'Система быстрых платежей (СБП)',
      logo: <SbpLogo />,
      description: 'Мгновенные переводы через СБП по номеру телефона или QR-коду. Поддерживается более 200 банков России.',
      features: [
        { label: 'Комиссия', value: '0%', highlight: true },
        { label: 'Время зачисления', value: 'Мгновенно' },
        { label: 'Мин. сумма', value: '100 ₽' },
        { label: 'Макс. сумма', value: '600 000 ₽/день' }
      ],
      supported: ['Сбербанк', 'Тинькофф', 'Альфа-Банк', 'ВТБ', 'Райффайзен', 'и 200+ других'],
      color: '#5C2D91',
      available: true
    },
    {
      id: 'mir',
      name: 'Карты МИР',
      logo: <MirLogo />,
      description: 'Национальная платёжная система России. Безопасные платежи картами МИР с технологией 3D Secure.',
      features: [
        { label: 'Комиссия', value: '1.5%' },
        { label: 'Время зачисления', value: '1-5 минут' },
        { label: 'Мин. сумма', value: '100 ₽' },
        { label: 'Макс. сумма', value: '500 000 ₽' }
      ],
      supported: ['Все карты МИР', '3D Secure', 'Токенизация'],
      color: '#4DB45E',
      available: true
    },
    {
      id: 'visa',
      name: 'Visa',
      logo: <VisaLogo />,
      description: 'Международная платёжная система. Принимаем карты Visa, выпущенные банками СНГ и других стран.',
      features: [
        { label: 'Комиссия', value: '2.5%' },
        { label: 'Время зачисления', value: '1-15 минут' },
        { label: 'Мин. сумма', value: '500 ₽' },
        { label: 'Макс. сумма', value: '300 000 ₽' }
      ],
      supported: ['Visa Classic', 'Visa Gold', 'Visa Platinum', '3D Secure'],
      color: '#1A1F71',
      available: true
    },
    {
      id: 'mastercard',
      name: 'Mastercard',
      logo: <MastercardLogo />,
      description: 'Международная платёжная система. Принимаем карты Mastercard, выпущенные банками СНГ.',
      features: [
        { label: 'Комиссия', value: '2.5%' },
        { label: 'Время зачисления', value: '1-15 минут' },
        { label: 'Мин. сумма', value: '500 ₽' },
        { label: 'Макс. сумма', value: '300 000 ₽' }
      ],
      supported: ['Mastercard Standard', 'Mastercard Gold', 'World Mastercard', '3D Secure'],
      color: '#EB001B',
      available: true
    },
    {
      id: 'mia',
      name: 'MIA (Молдова)',
      logo: <MiaLogo />,
      description: 'Платёжная система Молдовы. Мгновенные платежи через QR-код в молдавских леях (MDL).',
      features: [
        { label: 'Комиссия', value: '1%', highlight: true },
        { label: 'Время зачисления', value: 'Мгновенно' },
        { label: 'Мин. сумма', value: '10 MDL' },
        { label: 'Макс. сумма', value: '50 000 MDL' }
      ],
      supported: ['MAIB', 'Moldindconbank', 'VictoriaBank', 'и другие банки Молдовы'],
      color: '#00A651',
      available: true
    }
  ];

  const securityFeatures = [
    {
      icon: <Shield size={24} />,
      title: 'PCI DSS Level 1',
      description: 'Высший уровень сертификации безопасности платёжных карт'
    },
    {
      icon: <CheckCircle size={24} />,
      title: '3D Secure',
      description: 'Двухфакторная аутентификация для всех карточных платежей'
    },
    {
      icon: <Zap size={24} />,
      title: 'SSL/TLS',
      description: 'Шифрование всех данных при передаче'
    },
    {
      icon: <Globe size={24} />,
      title: 'AML/KYC',
      description: 'Проверка транзакций на соответствие требованиям законодательства'
    }
  ];

  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <header className="bg-white border-b border-gray-100 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 py-4 flex items-center justify-between">
          <a href="/" className="flex items-center gap-3">
            <img src="/polytech-logo.png" alt="PolyTech Wallet" className="w-10 h-10 rounded-xl" />
            <span className="font-bold text-xl" style={{ color: COLORS.dark }}>PolyTech Wallet</span>
          </a>
          <a 
            href="/"
            className="flex items-center gap-2 text-sm font-medium hover:text-green-600 transition"
            style={{ color: COLORS.gray }}
          >
            <ArrowLeft size={16} />
            На главную
          </a>
        </div>
      </header>

      {/* Hero Section */}
      <section className="py-16 md:py-20" style={{ background: `linear-gradient(135deg, ${COLORS.dark} 0%, ${COLORS.darkLight} 100%)` }}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.h1 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-4xl md:text-5xl font-bold text-white mb-6"
          >
            Способы оплаты
          </motion.h1>
          <motion.p 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="text-lg md:text-xl max-w-2xl mx-auto mb-8" 
            style={{ color: COLORS.grayLight }}
          >
            Мы поддерживаем различные платёжные системы для вашего удобства. 
            Выбирайте наиболее подходящий способ оплаты.
          </motion.p>
          
          {/* Payment logos */}
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="flex flex-wrap items-center justify-center gap-4"
          >
            {paymentMethods.map((method) => (
              <div key={method.id} className="bg-white/10 rounded-lg p-2">
                {method.logo}
              </div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* Payment Methods Grid */}
      <section className="py-16 md:py-24" style={{ background: COLORS.bg }}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="space-y-8">
            {paymentMethods.map((method, i) => (
              <motion.div
                key={method.id}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.1 }}
                className="bg-white rounded-2xl shadow-sm overflow-hidden"
              >
                <div className="p-6 md:p-8">
                  <div className="flex flex-col md:flex-row md:items-start gap-6">
                    {/* Logo & Name */}
                    <div className="flex items-center gap-4 md:w-64 flex-shrink-0">
                      <div className="w-16 flex-shrink-0">
                        {method.logo}
                      </div>
                      <div>
                        <h3 className="font-bold text-lg" style={{ color: COLORS.dark }}>{method.name}</h3>
                        {method.available ? (
                          <span className="inline-flex items-center gap-1 text-xs font-medium px-2 py-1 rounded-full bg-green-100 text-green-700">
                            <CheckCircle size={12} />
                            Доступно
                          </span>
                        ) : (
                          <span className="inline-flex items-center gap-1 text-xs font-medium px-2 py-1 rounded-full bg-gray-100 text-gray-500">
                            <Clock size={12} />
                            Скоро
                          </span>
                        )}
                      </div>
                    </div>

                    {/* Description */}
                    <div className="flex-1">
                      <p className="mb-4" style={{ color: COLORS.gray }}>{method.description}</p>
                      
                      {/* Features Grid */}
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
                        {method.features.map((feature, j) => (
                          <div key={j} className="p-3 rounded-xl" style={{ background: COLORS.bg }}>
                            <p className="text-xs mb-1" style={{ color: COLORS.grayLight }}>{feature.label}</p>
                            <p 
                              className={`font-semibold ${feature.highlight ? 'text-green-600' : ''}`}
                              style={{ color: feature.highlight ? undefined : COLORS.dark }}
                            >
                              {feature.value}
                            </p>
                          </div>
                        ))}
                      </div>

                      {/* Supported */}
                      <div className="flex flex-wrap gap-2">
                        {method.supported.map((item, j) => (
                          <span 
                            key={j} 
                            className="text-xs px-2 py-1 rounded-lg"
                            style={{ background: `${method.color}15`, color: method.color }}
                          >
                            {item}
                          </span>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* How to Pay Section */}
      <section className="py-16 md:py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold mb-4" style={{ color: COLORS.dark }}>
              Как пополнить кошелёк
            </h2>
            <p className="text-lg max-w-2xl mx-auto" style={{ color: COLORS.gray }}>
              Простой процесс в три шага
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {[
              {
                step: '1',
                title: 'Выберите сумму',
                description: 'Укажите сумму пополнения в рублях, леях или другой валюте',
                icon: <CreditCard size={32} />
              },
              {
                step: '2',
                title: 'Выберите способ оплаты',
                description: 'СБП, карты МИР, Visa, Mastercard или MIA (Молдова)',
                icon: <Smartphone size={32} />
              },
              {
                step: '3',
                title: 'Подтвердите платёж',
                description: 'Подтвердите оплату в приложении банка или по SMS',
                icon: <CheckCircle size={32} />
              }
            ].map((item, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.1 }}
                className="text-center relative"
              >
                {i < 2 && (
                  <div className="hidden md:block absolute top-12 left-1/2 w-full">
                    <ArrowRight size={24} style={{ color: COLORS.grayLight }} className="absolute -right-4" />
                  </div>
                )}
                <div 
                  className="w-24 h-24 rounded-2xl flex items-center justify-center mx-auto mb-6"
                  style={{ background: `${COLORS.primary}15`, color: COLORS.primary }}
                >
                  {item.icon}
                </div>
                <div 
                  className="w-8 h-8 rounded-full flex items-center justify-center mx-auto mb-4 text-white font-bold"
                  style={{ background: COLORS.primary }}
                >
                  {item.step}
                </div>
                <h3 className="text-xl font-semibold mb-3" style={{ color: COLORS.dark }}>{item.title}</h3>
                <p style={{ color: COLORS.gray }}>{item.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Security Section */}
      <section className="py-16 md:py-24" style={{ background: COLORS.dark }}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold mb-4 text-white">
              Безопасность платежей
            </h2>
            <p className="text-lg max-w-2xl mx-auto" style={{ color: COLORS.grayLight }}>
              Мы используем современные технологии защиты ваших данных
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {securityFeatures.map((feature, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.1 }}
                className="text-center p-6 rounded-2xl"
                style={{ background: 'rgba(255,255,255,0.05)' }}
              >
                <div 
                  className="w-12 h-12 rounded-xl flex items-center justify-center mx-auto mb-4"
                  style={{ background: `${COLORS.primary}20`, color: COLORS.primary }}
                >
                  {feature.icon}
                </div>
                <h3 className="text-lg font-semibold mb-2 text-white">{feature.title}</h3>
                <p className="text-sm" style={{ color: COLORS.grayLight }}>{feature.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Important Info */}
      <section className="py-16 md:py-20 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="rounded-2xl p-8" style={{ background: `${COLORS.primary}10`, border: `1px solid ${COLORS.primary}30` }}>
            <div className="flex items-start gap-4">
              <div 
                className="w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0"
                style={{ background: COLORS.primary, color: 'white' }}
              >
                <Info size={24} />
              </div>
              <div>
                <h3 className="text-xl font-semibold mb-3" style={{ color: COLORS.dark }}>
                  Важная информация
                </h3>
                <ul className="space-y-2" style={{ color: COLORS.gray }}>
                  <li className="flex items-start gap-2">
                    <CheckCircle size={16} style={{ color: COLORS.primary }} className="mt-1 flex-shrink-0" />
                    <span>Зачисление средств происходит автоматически после подтверждения платежа</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle size={16} style={{ color: COLORS.primary }} className="mt-1 flex-shrink-0" />
                    <span>Комиссии могут отличаться в зависимости от банка-эмитента карты</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle size={16} style={{ color: COLORS.primary }} className="mt-1 flex-shrink-0" />
                    <span>При возникновении проблем с платежом обратитесь в поддержку</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle size={16} style={{ color: COLORS.primary }} className="mt-1 flex-shrink-0" />
                    <span>Возврат средств осуществляется согласно политике возврата</span>
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16" style={{ background: `linear-gradient(135deg, ${COLORS.primary} 0%, ${COLORS.primaryDark} 100%)` }}>
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-2xl md:text-3xl font-bold mb-6 text-white">
            Готовы начать?
          </h2>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a 
              href="https://t.me/polytechex_bot" 
              target="_blank" 
              rel="noopener noreferrer"
              className="inline-flex items-center justify-center gap-2 px-8 py-4 rounded-2xl font-semibold transition bg-white hover:bg-gray-100"
              style={{ color: COLORS.primary }}
            >
              Открыть в Telegram
            </a>
            <a 
              href="/wallet"
              className="inline-flex items-center justify-center gap-2 px-8 py-4 rounded-2xl font-semibold transition border-2 border-white text-white hover:bg-white/10"
            >
              Веб-версия
            </a>
          </div>
        </div>
      </section>

      {/* Footer with payment logos */}
      <footer className="py-12 border-t border-gray-200 bg-white">
        <div className="max-w-7xl mx-auto px-4">
          <div className="flex flex-col items-center gap-6">
            <p className="text-sm" style={{ color: COLORS.gray }}>Принимаем к оплате:</p>
            <div className="flex flex-wrap items-center justify-center gap-4">
              <MirLogo />
              <SbpLogo />
              <VisaLogo />
              <MastercardLogo />
              <MiaLogo />
            </div>
            <p className="text-sm text-center" style={{ color: COLORS.grayLight }}>
              © 2017-2026 ОсОО «ФВА Финанс». Все права защищены.
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default PaymentMethodsPage;
